/* iTechVista — product
 *
 * Progressive enhancement over a form that already works without JavaScript:
 * the radios are real form controls inside the product form, and a <noscript>
 * <select name="id"> is rendered as the no-JS path. This script upgrades that
 * to live variant switching.
 *
 * What it does on an option change:
 *   - resolves the matching variant from the embedded variant JSON
 *   - updates the hidden id input, price, availability, inventory line and the
 *     add-to-cart label
 *   - marks option values that lead to no purchasable variant
 *   - swaps the gallery to the variant's image
 *   - updates the URL with ?variant= so the state is shareable and refresh-safe
 */
class ItvProduct extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('[data-variants]');
    if (!json) return;
    try {
      this.variants = JSON.parse(json.textContent);
    } catch (e) {
      return; // leave the no-JS form intact
    }

    this.form = this.querySelector('form');
    this.idInput = this.querySelector('[data-variant-id]');
    this.priceEl = this.querySelector('[data-price]');
    this.addBtn = this.querySelector('[data-add-button]');
    this.addLabel = this.querySelector('[data-add-label]');
    this.invEl = this.querySelector('[data-inventory]');
    this.radios = Array.from(this.querySelectorAll('.itv-product__radio'));

    this.radios.forEach((r) =>
      r.addEventListener('change', () => this.onOptionChange())
    );

    this.wireRail();
    this.querySelectorAll('[data-media-target]').forEach((btn) => {
      btn.addEventListener('click', () => this.showMedia(btn.dataset.mediaTarget));
    });

    this.wireCopy();
    this.wireZoom();
    this.markUnavailable();
  }

  selectedOptions() {
    const out = [];
    this.radios.forEach((r) => {
      if (r.checked) out[Number(r.dataset.optionIndex) - 1] = r.value;
    });
    return out;
  }

  findVariant(opts) {
    return this.variants.find((v) =>
      opts.every((val, i) => val === undefined || v.options[i] === val)
    );
  }

  onOptionChange() {
    const opts = this.selectedOptions();
    const variant = this.findVariant(opts);

    // Reflect the chosen value next to each option name.
    opts.forEach((val, i) => {
      const label = this.querySelector(`[data-opt-value="${i + 1}"]`);
      if (label && val) label.textContent = val;
    });

    this.markUnavailable();
    if (!variant) {
      this.setUnavailable();
      return;
    }

    if (this.idInput) {
      this.idInput.value = variant.id;
      this.idInput.disabled = !variant.available;
    }

    this.updatePrice(variant);
    this.updateAvailability(variant);
    this.showMediaForVariant(variant);

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }

  /* Grey out values that cannot combine into a purchasable variant. */
  markUnavailable() {
    const chosen = this.selectedOptions();
    this.radios.forEach((r) => {
      const i = Number(r.dataset.optionIndex) - 1;
      const probe = chosen.slice();
      probe[i] = r.value;
      const match = this.variants.find((v) =>
        probe.every((val, k) => val === undefined || v.options[k] === val)
      );
      const label = r.nextElementSibling;
      if (!label) return;
      label.classList.toggle('is-unavailable', !match || !match.available);
    });
  }

  updatePrice(variant) {
    if (!this.priceEl) return;
    const money = (cents) => {
      const fmt = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD',
      });
      return fmt.format(cents / 100);
    };
    const now = this.priceEl.querySelector('.itv-price__now');
    if (now) now.textContent = money(variant.price);

    const was = this.priceEl.querySelector('.itv-price__was');
    const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    if (was) was.style.display = onSale ? '' : 'none';
    if (was && onSale) {
      // keep the visually-hidden "Regular price" label intact
      const hidden = was.querySelector('.itv-visually-hidden');
      was.textContent = '';
      if (hidden) was.appendChild(hidden);
      was.append(money(variant.compare_at_price));
    }
    const badge = this.priceEl.querySelector('.itv-price__badge');
    if (badge) {
      badge.style.display = onSale ? '' : 'none';
      if (onSale) {
        const pct = Math.round(
          ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
        );
        badge.textContent = `−${pct}%`;
      }
    }
    const sold = this.priceEl.querySelector('.itv-price__sold');
    if (sold) sold.style.display = variant.available ? 'none' : '';
  }

  updateAvailability(variant) {
    if (this.addBtn) this.addBtn.disabled = !variant.available;
    if (this.addLabel) {
      this.addLabel.textContent = variant.available
        ? this.addLabel.dataset.inStock || 'Add to cart'
        : 'Sold out';
    }
    if (this.invEl) {
      this.invEl.hidden = false;
      const span = this.invEl.querySelector('span');
      if (span) {
        span.className = variant.available ? 'is-ok' : 'is-out';
        span.textContent = variant.available ? 'In stock' : 'Sold out';
      }
    }
  }

  setUnavailable() {
    if (this.addBtn) this.addBtn.disabled = true;
    if (this.addLabel) this.addLabel.textContent = 'Unavailable';
    if (this.idInput) this.idInput.disabled = true;
  }

  showMediaForVariant(variant) {
    if (!variant.featured_media) return;
    this.showMedia(String(variant.featured_media.id));
  }

  /* Scroll arrows for the thumbnail rail. They only appear when the rail can
   * actually scroll -- an arrow on a three-image product would be a control
   * that does nothing. Re-checked on resize because the rail is horizontal on
   * narrow screens and vertical on wide ones. */
  wireRail() {
    const wrap = this.querySelector('.itv-product__thumbs-wrap');
    const rail = this.querySelector('.itv-product__thumbs');
    if (!wrap || !rail) return;

    const horizontal = () => rail.scrollWidth > rail.clientWidth + 1;
    const vertical = () => rail.scrollHeight > rail.clientHeight + 1;

    const sync = () => {
      const can = horizontal() || vertical();
      wrap.classList.toggle('is-scrollable', can);
      wrap.querySelectorAll('[data-rail]').forEach((b) => { b.hidden = !can; });
    };

    wrap.querySelectorAll('[data-rail]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const dir = btn.dataset.rail === 'up' ? -1 : 1;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const opts = { behavior: reduce ? 'auto' : 'smooth' };
        if (vertical()) opts.top = dir * Math.max(120, rail.clientHeight * 0.8);
        else opts.left = dir * Math.max(120, rail.clientWidth * 0.8);
        rail.scrollBy(opts);
      });
    });

    sync();
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(sync);
      ro.observe(rail);
    } else {
      window.addEventListener('resize', sync);
    }
  }

  showMedia(id) {
    /* `hidden` cannot be transitioned -- it is display:none, so the old slide
     * vanished and the new one appeared in the same frame. That hard cut is why
     * switching felt abrupt. Cross-fade instead: reveal the incoming slide
     * immediately, let CSS fade it, and only mark the outgoing one `hidden`
     * once its fade has finished, so it stays out of the a11y tree and out of
     * tab order without killing the animation. */
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const FADE = reduce ? 0 : 260;

    this.querySelectorAll('[data-media-id]').forEach((s) => {
      const on = s.dataset.mediaId === String(id);
      if (on) {
        if (s._fadeTimer) { clearTimeout(s._fadeTimer); s._fadeTimer = null; }
        s.hidden = false;
        /* Every slide after the first ships `loading="lazy"`, so the moment it
         * is revealed the browser still has to fetch and decode it. Measured on
         * a 14-image product: ~270ms passed between the click and any visible
         * change, and both slides sat at opacity 0 in between -- a blank flash.
         * Promote it and wait for the decode, then fade. `decode()` resolves
         * immediately for an image already in cache, so repeat visits to a
         * slide are instant. */
        const img = s.querySelector('img');
        const reveal = () => {
          void s.offsetWidth;   // give the transition a start value
          s.classList.add('is-active');
        };
        if (img && !img.complete) {
          img.loading = 'eager';
          if (typeof img.decode === 'function') {
            img.decode().then(reveal).catch(reveal);
          } else {
            img.addEventListener('load', reveal, { once: true });
            img.addEventListener('error', reveal, { once: true });
          }
        } else {
          reveal();
        }
      } else if (!s.hidden) {
        s.classList.remove('is-active');
        if (s._fadeTimer) clearTimeout(s._fadeTimer);
        s._fadeTimer = setTimeout(() => { s.hidden = true; s._fadeTimer = null; }, FADE);
      }
    });

    let active = null;
    this.querySelectorAll('[data-media-target]').forEach((b) => {
      const on = b.dataset.mediaTarget === String(id);
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', String(on));
      if (on) active = b;
    });

    /* Warm the neighbouring slides so the NEXT switch has nothing to wait for.
     * Only the immediate neighbours: eager-loading all 14 images of a product
     * would undo the point of lazy loading. */
    const slides = [...this.querySelectorAll('[data-media-id]')];
    const at = slides.findIndex((s) => s.dataset.mediaId === String(id));
    [at - 1, at + 1].forEach((n) => {
      const s = slides[n];
      if (!s) return;
      const img = s.querySelector('img');
      if (img && !img.complete) img.loading = 'eager';
    });

    /* Follow the main image: with a long rail the active thumbnail could sit
     * far outside the visible strip, so the list appeared not to respond at
     * all. `nearest` scrolls only when it actually needs to. */
    if (active && typeof active.scrollIntoView === 'function') {
      try {
        active.scrollIntoView({
          block: 'nearest', inline: 'nearest',
          behavior: reduce ? 'auto' : 'smooth'
        });
      } catch (e) { active.scrollIntoView(false); }
    }
  }

  /* Click-to-zoom. A real <dialog> so Escape, focus trapping and the backdrop
   * come from the platform rather than being re-implemented badly. */
  wireZoom() {
    if (this.dataset.zoom !== 'lightbox') return;
    const stage = this.querySelector('.itv-product__stage');
    if (!stage || typeof HTMLDialogElement === 'undefined') return;

    const dialog = document.createElement('dialog');
    dialog.className = 'itv-product__lightbox';
    dialog.innerHTML =
      '<button type="button" class="itv-product__lbclose" aria-label="Close">×</button>' +
      '<img alt="">';
    this.appendChild(dialog);
    const img = dialog.querySelector('img');

    stage.addEventListener('click', (e) => {
      const source = e.target.closest('img');
      if (!source) return;
      img.src = source.currentSrc || source.src;
      img.alt = source.alt || '';
      dialog.showModal();
    });
    dialog.addEventListener('click', (e) => {
      if (e.target === dialog || e.target.closest('.itv-product__lbclose')) dialog.close();
    });
    stage.classList.add('is-zoomable');
  }

  wireCopy() {
    const btn = this.querySelector('[data-copy]');
    const input = this.querySelector('[data-share-url]');
    const done = this.querySelector('[data-copied]');
    if (!btn || !input) return;
    btn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(input.value);
      } catch (e) {
        input.select();
        return; // selection is a usable fallback; do not claim success
      }
      if (done) {
        done.hidden = false;
        setTimeout(() => { done.hidden = true; }, 2500);
      }
    });
  }
}

if (!customElements.get('itv-product')) {
  customElements.define('itv-product', ItvProduct);
}
